import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, CheckCircle2, ArrowLeft, Mail } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";

const errMsg = (e, d) => {
  const x = e?.response?.data?.detail;
  return typeof x === "string" ? x : Array.isArray(x) ? x.map((i) => i.msg).join(" ") : d;
};
const inputCls = "mt-1.5 h-11 rounded-xl border-slate-200";

const NuevaClave = ({ busy, onSubmit, children }) => {
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const enviar = (e) => {
    e.preventDefault();
    if (p1.length < 8) return toast.error("La contraseña debe tener al menos 8 caracteres");
    if (p1 !== p2) return toast.error("Las contraseñas no coinciden");
    onSubmit(p1);
  };
  return (
    <form onSubmit={enviar} className="space-y-4" data-testid="reset-new-password-form">
      {children}
      <div><Label>Nueva contraseña</Label><PasswordInput data-testid="reset-password-p1" value={p1} onChange={(e) => setP1(e.target.value)} placeholder="Mínimo 8 caracteres" className={inputCls} /></div>
      <div><Label>Repite la contraseña</Label><PasswordInput data-testid="reset-password-p2" value={p2} onChange={(e) => setP2(e.target.value)} className={inputCls} /></div>
      <Button data-testid="reset-password-submit" disabled={busy} className="w-full h-11 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-semibold">
        {busy ? "Guardando..." : "Guardar nueva contraseña"}
      </Button>
    </form>
  );
};

export default function RecuperarClave() {
  const [sp] = useSearchParams();
  const token = sp.get("t") || "";
  const [paso, setPaso] = useState(token ? "VERIFICANDO" : "EMAIL"); // EMAIL | CODIGO | TOKEN | OK | INVALIDO
  const [email, setEmail] = useState("");
  const [codigo, setCodigo] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    publicApi.post("/auth/reset-password/verify", { token })
      .then((r) => { setEmail(r.data.email); setPaso("TOKEN"); })
      .catch(() => setPaso("INVALIDO"));
  }, [token]);

  const solicitar = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const r = await publicApi.post("/auth/forgot-password", { email });
      toast.success(r.data.mensaje);
      setPaso("CODIGO");
    } catch (er) { toast.error(errMsg(er, "No se pudo enviar el correo")); }
    finally { setBusy(false); }
  };

  const guardar = async (nueva) => {
    setBusy(true);
    try {
      await publicApi.post("/auth/reset-password", token ? { token, nueva } : { email, codigo: codigo.trim(), nueva });
      setPaso("OK");
    } catch (er) { toast.error(errMsg(er, "No se pudo cambiar la contraseña")); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 grid place-items-center px-4" data-testid="recuperar-clave-page">
      <div className="w-full max-w-md bg-white rounded-3xl border border-slate-100 p-8 shadow-sm space-y-5">
        <Link to="/login" data-testid="reset-back-login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 font-medium">
          <ArrowLeft className="w-4 h-4" /> Volver a ingresar
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 font-display flex items-center gap-2"><KeyRound className="w-6 h-6 text-sky-600" /> Recuperar contraseña</h1>

        {paso === "VERIFICANDO" && <p className="text-sm text-slate-500">Validando enlace...</p>}

        {paso === "INVALIDO" && (
          <div className="space-y-3" data-testid="reset-invalid-link">
            <p className="text-sm text-rose-600">El enlace no es válido o ya venció. Solicita uno nuevo.</p>
            <Link to="/recuperar-clave"><Button className="w-full rounded-xl" variant="outline">Solicitar nuevo enlace</Button></Link>
          </div>
        )}

        {paso === "EMAIL" && (
          <form onSubmit={solicitar} className="space-y-4" data-testid="reset-email-form">
            <p className="text-sm text-slate-600">Ingresa el correo de tu cuenta. Te enviaremos un enlace y un código de 6 dígitos, válidos por 30 minutos.</p>
            <div><Label>Email</Label><Input data-testid="reset-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@empresa.cl" className={inputCls} /></div>
            <Button data-testid="reset-email-submit" disabled={busy} className="w-full h-11 rounded-xl bg-sky-500 hover:bg-sky-600 text-white font-semibold">
              <Mail className="w-4 h-4 mr-2" /> {busy ? "Enviando..." : "Enviar instrucciones"}
            </Button>
          </form>
        )}

        {paso === "CODIGO" && (
          <NuevaClave busy={busy} onSubmit={guardar}>
            <p className="text-sm text-slate-600">Revisa <strong>{email}</strong>. Puedes abrir el enlace del correo o escribir aquí el código.</p>
            <div>
              <Label>Código de 6 dígitos</Label>
              <Input data-testid="reset-code-input" inputMode="numeric" maxLength={6} value={codigo}
                     onChange={(e) => setCodigo(e.target.value.replace(/\D/g, ""))}
                     className={`${inputCls} text-center text-2xl tracking-[0.5em] font-mono-num`} placeholder="000000" />
            </div>
            <button type="button" data-testid="reset-resend" disabled={busy} onClick={solicitar} className="text-xs font-semibold text-sky-600 hover:text-sky-700">
              ¿No llegó? Reenviar correo
            </button>
          </NuevaClave>
        )}

        {paso === "TOKEN" && (
          <NuevaClave busy={busy} onSubmit={guardar}>
            <p className="text-sm text-slate-600">Define la nueva contraseña para <strong data-testid="reset-token-email">{email}</strong>.</p>
          </NuevaClave>
        )}

        {paso === "OK" && (
          <div className="text-center space-y-3" data-testid="reset-ok">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <p className="font-bold text-slate-900">Contraseña actualizada</p>
            <p className="text-sm text-slate-500">Ya puedes ingresar con tu nueva contraseña.</p>
            <Link to="/login"><Button data-testid="reset-go-login" className="w-full rounded-xl bg-slate-900 text-white">Ingresar</Button></Link>
          </div>
        )}
      </div>
    </div>
  );
}
